import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package, Truck, Car, Home, Wrench, Users, Baby, Sparkles, ChefHat, Clock,
  MapPin, Loader2, FileText, ArrowLeft, Navigation, Star, X, User, Phone,
  MessageCircle, CheckCircle2, Activity, DollarSign, ChevronRight, Snowflake, Trees, XCircle, Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ReviewModal from "@/components/ReviewModal";
import ReceiptModal from "@/components/ReceiptModal";
import axios from "axios";



const SERVICE_CONFIG = {
  cargo: { label: "Cargo Delivery", icon: Truck, color: "bg-lime-500" },
  courier: { label: "Courier", icon: Package, color: "bg-sky-500" },
  towing: { label: "Towing", icon: Car, color: "bg-orange-500" },
  movers: { label: "Movers", icon: Home, color: "bg-purple-500" },
  roadside: { label: "Roadside", icon: Wrench, color: "bg-red-500" },
  laborer: { label: "Laborer", icon: Users, color: "bg-rose-500" },
  nanny: { label: "Nanny", icon: Baby, color: "bg-pink-500" },
  cleaning: { label: "Cleaning", icon: Sparkles, color: "bg-cyan-500" },
  private_chef: { label: "Private Chef", icon: ChefHat, color: "bg-amber-500" },
  snow_plowing: { label: "Snow Plowing", icon: Snowflake, color: "bg-blue-500" },
  lawn_mowing: { label: "Lawn Mowing", icon: Trees, color: "bg-green-500" },
};

const STATUS_CONFIG = {
  pending: { color: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-400", label: "Pending" },
  accepted: { color: "bg-blue-100 text-blue-800 border-blue-200", dot: "bg-blue-400", label: "Accepted" },
  booked: { color: "bg-violet-100 text-violet-800 border-violet-200", dot: "bg-violet-400", label: "Booked" },
  completed: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-400", label: "Completed" },
  cancelled: { color: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-400", label: "Cancelled" },
};

const TIMELINE_STEPS = ["pending", "accepted", "booked", "completed"];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const BookingDetailModal = ({ booking, onClose, onTrack, onReview, onCancel, onViewReceipt, navigate }) => {
  if (!booking) return null;
  const config = SERVICE_CONFIG[booking.service_type] || SERVICE_CONFIG.cargo;
  const ServiceIcon = config.icon;
  const es = booking.effective_status || "pending";
  const currentStepIndex = TIMELINE_STEPS.indexOf(es === "cancelled" ? "pending" : es);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="booking-detail-overlay"
    >
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        data-testid="booking-detail-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-100 p-4 flex items-center justify-between rounded-t-3xl z-10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${config.color} rounded-xl flex items-center justify-center`}>
              <ServiceIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-950">{config.label}</h3>
              <StatusBadge status={es} />
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center" data-testid="close-detail-btn">
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Timeline */}
          {es !== "cancelled" && (
            <div data-testid="booking-timeline">
              <p className="text-xs font-semibold text-stone-500 mb-3 uppercase tracking-wider">Progress</p>
              <div className="flex items-center justify-between relative">
                <div className="absolute top-3 left-6 right-6 h-0.5 bg-stone-200" />
                <div className="absolute top-3 left-6 h-0.5 bg-emerald-500 transition-all" style={{ width: `${Math.max(0, (currentStepIndex / (TIMELINE_STEPS.length - 1)) * 100)}%`, maxWidth: "calc(100% - 48px)" }} />
                {TIMELINE_STEPS.map((step, i) => {
                  const isActive = i <= currentStepIndex;
                  const isCurrent = i === currentStepIndex;
                  return (
                    <div key={step} className="flex flex-col items-center z-10 relative">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isCurrent ? "bg-emerald-500 border-emerald-500 ring-4 ring-emerald-100" :
                        isActive ? "bg-emerald-500 border-emerald-500" : "bg-white border-stone-300"
                      }`}>
                        {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <span className={`text-xs mt-1.5 capitalize ${isCurrent ? "font-semibold text-emerald-700" : isActive ? "text-emerald-600" : "text-stone-400"}`}>{step}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Live Tracking Banner */}
          {es === "accepted" && booking.trip_status && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="live-tracking-banner">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-blue-600 animate-pulse" />
                <div>
                  <p className="font-semibold text-blue-800">
                    {booking.trip_status === "en_route" ? "Professional is on the way" :
                     booking.trip_status === "arriving" ? "Professional is arriving" :
                     booking.trip_status === "arrived" ? "Professional has arrived" :
                     "In progress"}
                  </p>
                  <p className="text-sm text-blue-600">Track their live location</p>
                </div>
              </div>
            </div>
          )}

          {/* Professional Info */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="professional-info">
            <p className="text-xs font-semibold text-stone-500 mb-3 uppercase tracking-wider">Professional</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                {booking.professional_picture ? (
                  <img src={booking.professional_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-emerald-600" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-emerald-950">{booking.professional_name || "Not assigned"}</p>
                <p className="text-xs text-stone-500">
                  {booking.professional_id ? "Assigned" : "Awaiting assignment"}
                </p>
              </div>
            </div>
          </div>

          {/* Service Info */}
          <div className="bg-stone-50 rounded-2xl p-4" data-testid="service-info">
            <p className="text-xs font-semibold text-stone-500 mb-3 uppercase tracking-wider">Service Details</p>
            <div className="space-y-2.5 text-sm">
              {booking.detail && (
                <div className="flex justify-between">
                  <span className="text-stone-500">Details</span>
                  <span className="text-stone-800 font-medium text-right max-w-[60%]">{booking.detail}</span>
                </div>
              )}
              {(booking.service_date || booking.service_time) && (
                <div className="flex justify-between">
                  <span className="text-stone-500">Date & Time</span>
                  <span className="text-stone-800 font-medium">{booking.service_date} {booking.service_time}</span>
                </div>
              )}
              {booking.location && (
                <div className="flex justify-between">
                  <span className="text-stone-500">Location</span>
                  <span className="text-stone-800 font-medium text-right max-w-[60%] truncate">{booking.location}</span>
                </div>
              )}
              {booking.price && (
                <div className="flex justify-between">
                  <span className="text-stone-500">Price</span>
                  <span className="text-emerald-700 font-bold">{booking.price}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-stone-500">Created</span>
                <span className="text-stone-800">{new Date(booking.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2" data-testid="action-buttons">
            {es === "pending" && (
              <Button
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => onCancel(booking)}
                data-testid="cancel-booking-btn"
              >
                <XCircle className="w-4 h-4 mr-2" /> Cancel Booking
              </Button>
            )}
            {es === "accepted" && booking.request_id && (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => { onClose(); onTrack(booking.request_id); }}
                data-testid="track-live-btn"
              >
                <Navigation className="w-4 h-4 mr-2" /> Track Live
              </Button>
            )}
            {es === "accepted" && booking.professional_id && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { onClose(); navigate("/inbox"); }}
                data-testid="contact-professional-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Contact Professional
              </Button>
            )}
            {es === "completed" && (
              <>
                <Button
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => { onClose(); onReview(booking); }}
                  data-testid="leave-review-btn"
                >
                  <Star className="w-4 h-4 mr-2" /> Leave Review
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => { onClose(); onViewReceipt(booking); }}
                  data-testid="view-receipt-btn"
                >
                  <Receipt className="w-4 h-4 mr-2" /> View Receipt
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => { onClose(); navigate("/"); }}
                  data-testid="rebook-btn"
                >
                  <Package className="w-4 h-4 mr-2" /> Rebook Service
                </Button>
              </>
            )}
            {(es === "accepted" || es === "booked") && (
              <Button
                variant="outline"
                className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => { onClose(); onViewReceipt(booking); }}
                data-testid="view-receipt-active-btn"
              >
                <Receipt className="w-4 h-4 mr-2" /> View Receipt
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MyBookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reviewTarget, setReviewTarget] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
    fetchPendingReviews();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/user/bookings`, { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/pending`, { withCredentials: true });
      setPendingReviews(response.data);
    } catch {}
  };

  const handleCancelBooking = async (booking) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
      await axios.post(`${API}/user/bookings/${booking.id}/cancel`, {}, { withCredentials: true });
      toast.success("Booking cancelled");
      setSelectedBooking(null);
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel booking");
    }
  };

  const handleViewReceipt = async (booking) => {
    setReceiptLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/${booking.id}/receipt`, { withCredentials: true });
      setReceiptData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Receipt not available for this booking");
    } finally {
      setReceiptLoading(false);
    }
  };

  const filteredBookings = bookings.filter(b => {
    const es = b.effective_status || b.status;
    if (statusFilter === "all") return true;
    if (statusFilter === "active") return es === "accepted" || es === "booked";
    return es === statusFilter;
  });

  const stats = {
    all: bookings.length,
    active: bookings.filter(b => ["accepted", "booked"].includes(b.effective_status)).length,
    completed: bookings.filter(b => b.effective_status === "completed").length,
    cancelled: bookings.filter(b => b.effective_status === "cancelled").length,
    pending: bookings.filter(b => b.effective_status === "pending").length,
  };

  const tabs = [
    { key: "all", label: "All", count: stats.all },
    { key: "active", label: "Active", count: stats.active },
    { key: "completed", label: "Completed", count: stats.completed },
    { key: "cancelled", label: "Cancelled", count: stats.cancelled },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try { return new Date(dateStr).toLocaleDateString("en-CA", { month: "short", day: "numeric" }); }
    catch { return dateStr; }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="my-bookings-page">
      <Navbar />

      {/* Header */}
      <section className="relative pt-28 pb-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-950" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-emerald-300 hover:text-white mb-3 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-lime-400 rounded-2xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-emerald-950" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">My Bookings</h1>
              <p className="text-emerald-300 text-sm">{bookings.length} total bookings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <div className="sticky top-0 z-30 bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto" data-testid="filter-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  statusFilter === tab.key
                    ? "bg-emerald-950 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
                data-testid={`filter-${tab.key}`}
              >
                {tab.label}
                <span className="ml-1.5 text-xs opacity-70">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-6 px-4">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              <span className="text-stone-500">Loading bookings...</span>
            </div>
          ) : filteredBookings.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20" data-testid="empty-state">
              <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 font-medium">No bookings found</p>
              <p className="text-sm text-stone-400 mt-1 mb-4">
                {statusFilter === "all" ? "Book a service to get started!" : "No bookings with this status."}
              </p>
              <Link to="/">
                <Button className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-full px-6" data-testid="browse-services-btn">Browse Services</Button>
              </Link>
            </motion.div>
          ) : (
            <div className="space-y-3" data-testid="bookings-list">
              <AnimatePresence mode="popLayout">
                {filteredBookings.map((booking, index) => {
                  const config = SERVICE_CONFIG[booking.service_type] || SERVICE_CONFIG.cargo;
                  const ServiceIcon = config.icon;
                  const es = booking.effective_status || "pending";

                  return (
                    <motion.div
                      key={booking.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: index * 0.02 }}
                      onClick={() => setSelectedBooking(booking)}
                      className="bg-white rounded-2xl border border-stone-100 p-4 cursor-pointer hover:border-emerald-200 hover:shadow-sm transition-all"
                      data-testid={`booking-card-${booking.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 ${config.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <ServiceIcon className="w-5 h-5 text-white" />
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Top: Service + Status */}
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-semibold text-emerald-950 text-sm">{config.label}</span>
                            <StatusBadge status={es} />
                          </div>
                          {/* Middle: Date + Professional */}
                          <div className="flex items-center gap-3 text-xs text-stone-500 mb-1">
                            {(booking.service_date || booking.created_at) && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {booking.service_date || formatDate(booking.created_at)}
                                {booking.service_time && ` ${booking.service_time}`}
                              </span>
                            )}
                            {booking.professional_name && booking.professional_name !== "Not assigned" && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {booking.professional_name}
                              </span>
                            )}
                          </div>
                          {/* Bottom: Location + Price */}
                          <div className="flex items-center justify-between">
                            {booking.location && (
                              <span className="text-xs text-stone-400 truncate flex items-center gap-1 max-w-[65%]">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                {booking.location.substring(0, 40)}{booking.location.length > 40 ? "..." : ""}
                              </span>
                            )}
                            {booking.price && (
                              <span className="text-sm font-bold text-emerald-700 flex-shrink-0" data-testid={`price-${booking.id}`}>
                                {booking.price}
                              </span>
                            )}
                          </div>
                        </div>

                        <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
                      </div>

                      {/* Quick action for active bookings */}
                      {es === "accepted" && booking.request_id && (
                        <Button
                          size="sm"
                          className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                          onClick={(e) => { e.stopPropagation(); navigate(`/tracking/${booking.request_id}`); }}
                          data-testid={`track-btn-${booking.id}`}
                        >
                          <Navigation className="w-3.5 h-3.5 mr-1.5" /> Track Live
                        </Button>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedBooking && (
          <BookingDetailModal
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
            onTrack={(reqId) => navigate(`/tracking/${reqId}`)}
            onCancel={handleCancelBooking}
            onViewReceipt={handleViewReceipt}
            onReview={(booking) => {
              const pr = pendingReviews.find(p => p.booking_id === booking.id || p.request_id === booking.request_id);
              if (pr) {
                setReviewTarget({
                  requestId: pr.request_id,
                  revieweeName: pr.reviewee_name || "your provider",
                  revieweeRole: pr.reviewee_role || "professional",
                });
              } else {
                toast.info("Review already submitted or not available yet.");
              }
            }}
            navigate={navigate}
          />
        )}
      </AnimatePresence>

      {/* Review Modal */}
      {reviewTarget && (
        <ReviewModal
          requestId={reviewTarget.requestId}
          revieweeName={reviewTarget.revieweeName}
          revieweeRole={reviewTarget.revieweeRole}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => { fetchPendingReviews(); setReviewTarget(null); }}
        />
      )}

      {/* Receipt Modal */}
      <AnimatePresence>
        {receiptData && (
          <ReceiptModal
            receipt={receiptData}
            onClose={() => setReceiptData(null)}
          />
        )}
      </AnimatePresence>

      {/* Receipt Loading Overlay */}
      {receiptLoading && (
        <div className="fixed inset-0 z-[55] bg-black/30 flex items-center justify-center" data-testid="receipt-loading">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
            <span className="text-stone-700 font-medium">Loading receipt...</span>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MyBookingsPage;
